import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import { BLOCKLY_COLORS } from '../core/blockly-types.ts';
import { createPlusMinusField, registerMutatorIfMissing } from './blockly-field-factory.ts';
import { getValueCode } from './text-join-plus-minus.ts';

// Mutator mixin: adds/removes IMG0..N value inputs (each a reference-image slot) via +/- buttons.
const AiImageRefImagesMixin: any = {
    imageCount_: 0,

    updateShape_: function (): void {
        if (this.getInput('BUTTONS_ROW')) {
            this.removeInput('BUTTONS_ROW');
        }
        let i = this.imageCount_;
        while (this.getInput('IMG' + i)) {
            this.removeInput('IMG' + i);
            i++;
        }
        for (let j = 0; j < this.imageCount_; j++) {
            if (!this.getInput('IMG' + j)) {
                const input = this.appendValueInput('IMG' + j).setCheck(['IMAGE', 'URL']);
                if (j === 0) input.appendField('Referenzbilder:');
            }
        }
        const buttonsInput = this.appendDummyInput('BUTTONS_ROW');
        buttonsInput.appendField(createPlusMinusField(true), 'PLUS');
        buttonsInput.appendField(createPlusMinusField(false), 'MINUS');
    },

    addInput_: function (): void {
        this.imageCount_++;
        this.updateShape_();
        if (this.rendered) {
            this.render();
            this.bumpNeighbours();
        }
    },

    removeInput_: function (): void {
        if (this.imageCount_ > 0) {
            const lastInput = this.getInput('IMG' + (this.imageCount_ - 1));
            if (lastInput?.connection?.targetBlock()) {
                lastInput.connection.targetBlock().unplug(true);
            }
            this.imageCount_--;
            this.updateShape_();
            if (this.rendered) {
                this.render();
                this.bumpNeighbours();
            }
        }
    },

    saveExtraState: function (): any {
        return { imageCount: this.imageCount_ };
    },

    loadExtraState: function (state: any): void {
        this.imageCount_ = state?.imageCount ?? 0;
        this.updateShape_();
    },

    mutationToDom: function (): Element {
        const mutation = Blockly.utils.xml.createElement('mutation');
        mutation.setAttribute('items', String(this.imageCount_));
        return mutation;
    },

    domToMutation: function (xmlElement: Element): void {
        const items = xmlElement.getAttribute('items');
        this.imageCount_ = items ? parseInt(items, 10) : 0;
        this.updateShape_();
    },
};

let _aiImageGenerateDetailedRegistered = false;

// Registers the AIIMAGE_GENERATE_DETAILED block: engine/aspect-ratio/quality dropdowns, a prompt
// input, and a +/- mutator for 0..N reference-image inputs (like CARD_BUTTONS/DOC_SEARCH_NEW_V2).
export function registerAiImageGenerateDetailed(): void {
    if (_aiImageGenerateDetailedRegistered) return;
    _aiImageGenerateDetailedRegistered = true;

    registerMutatorIfMissing('aiimage_ref_images_mutator', AiImageRefImagesMixin, undefined, []);

    Blockly.defineBlocksWithJsonArray([
        {
            type: 'AIIMAGE_GENERATE_DETAILED',
            message0: '🖼️ KI-Bild generieren\nEngine: %1\nSeitenverhältnis: %2\nQualität (nur OpenAI): %3\nPrompt: %4',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'ENGINE',
                    options: [
                        ['OpenAI', 'RAGAI.AIIMAGE_OPENAI'],
                        ['Fal.ai', 'RAGAI.AIIMAGE_FALAI'],
                        ['Stable Diffusion', 'RAGAI.AIIMAGE_STABLEDIFFUSION'],
                    ],
                },
                {
                    type: 'field_dropdown',
                    name: 'ASPECTRATIO',
                    options: [['1:1', '1:1'], ['16:9', '16:9'], ['9:16', '9:16'], ['4:3', '4:3'], ['3:4', '3:4']],
                },
                {
                    type: 'field_dropdown',
                    name: 'QUALITY',
                    options: [['Automatisch', 'auto'], ['Niedrig', 'low'], ['Mittel', 'medium'], ['Hoch', 'high']],
                },
                // Kein check: akzeptiert auch Variablen o.ä. mit untypisiertem Output, nicht nur reinen Text.
                { type: 'input_value', name: 'PROMPT' },
            ],
            output: 'URL',
            inputsInline: false,
            colour: BLOCKLY_COLORS.CARDS,
            mutator: 'aiimage_ref_images_mutator',
            tooltip: 'Generiert ein KI-Bild wahlweise mit OpenAI, fal.ai oder Stable Diffusion. Über das Plus-Symbol können beliebig viele Referenzbilder hinzugefügt werden (Bild-zu-Bild).',
        },
    ]);

    const originalInit = Blockly.Blocks['AIIMAGE_GENERATE_DETAILED'].init;
    Blockly.Blocks['AIIMAGE_GENERATE_DETAILED'].init = function (this: any) {
        originalInit.call(this);
        this.imageCount_ = 0;
        this.updateShape_();
    };

    javascriptGenerator.forBlock['AIIMAGE_GENERATE_DETAILED'] = function (block) {
        const engine = block.getFieldValue('ENGINE') || 'RAGAI.AIIMAGE_OPENAI';
        const aspectRatio = block.getFieldValue('ASPECTRATIO') || '1:1';
        const quality = block.getFieldValue('QUALITY') || 'auto';
        const prompt = getValueCode(block, 'PROMPT') || "''";

        const images: string[] = [];
        let i = 0;
        while (block.getInput('IMG' + i)) {
            const code = getValueCode(block, 'IMG' + i);
            if (code) images.push(`await RAGAI.HELPER.getImageURlFromSkill(${code})`);
            i++;
        }
        const imagesList = `[${images.join(', ')}]`;

        const code = `(await (async () => {
  const created = await ${engine}.create(${prompt}, { images: ${imagesList}, aspectRatio: '${aspectRatio}', quality: '${quality}' });
  const imageUrl = await ${engine}.getImageUrl(created._id);
  return imageUrl && imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl;
})())`;
        return [code, 0];
    };
}
